import type { FC } from 'react';

import { useContent, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { getStaticPrefix } from '@eneco/common/env';
import { useDevice } from '@eneco/hooks/deviceContext';
import { Box, Image, Stack, Text, VisuallyHidden } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';

import type { StoreButtonsContent } from './store-buttons-content.types';

/**
 * `StoreButtons` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/StoreButtons/StoreButtons.tsx`):
 * an optional title above the iOS/Android store badges, hidden per-platform (`useDevice`) and laid out
 * as a column in the `footer` variant or a row otherwise.
 *
 * Known simplification: see `store-buttons-content.types.ts` — `storeButtonsItem` resolves to Sanity's
 * `migrationUnresolvedReference` placeholder today, so neither badge has a link to render on
 * `?_cms=sanity` yet; the Sitecore lane is unaffected.
 */
const StoreButtons: FC = () => {
  const content = useContent<StoreButtonsContent>();
  const { isIOS, isAndroid } = useDevice();

  const displayVariant = useSetting('variant');
  const alignment = useSetting('alignment');
  const imageHeight = '48px';
  const imageWidth = displayVariant === 'footer' ? '144px' : '100%';

  const isMobile = !useMediaQuery('md');
  const horizontalAlign = alignment === 'right' && !isMobile ? 'end' : 'start';

  const iosLink = linkWithUrl(content.storeButtonsItem?.iosButtonLink);
  const androidLink = linkWithUrl(content.storeButtonsItem?.androidButtonLink);

  if (!iosLink && !androidLink) return null;

  return (
    <Stack gap="2" direction="column" alignX={horizontalAlign}>
      {content.title && (
        <Stack.Item>
          <Box paddingTop="6">
            <Text color="textPrimary" weight="bold">
              {content.title}
            </Text>
          </Box>
        </Stack.Item>
      )}
      <Stack.Item>
        <Stack gap="5" direction={displayVariant === 'footer' ? 'column' : 'row'}>
          {/* On Desktop always show the buttons */}
          {!isAndroid && iosLink?.href && (
            <Stack.Item>
              <a href={iosLink.href} target={iosLink.target} rel="noreferrer">
                <VisuallyHidden>{iosLink.text}</VisuallyHidden>
                <Image
                  alt={iosLink.text || 'Download in de App Store'}
                  height={imageHeight}
                  width={imageWidth}
                  src={`${getStaticPrefix()}/images/eneco/apple-store-badge.png`}
                />
              </a>
            </Stack.Item>
          )}
          {!isIOS && androidLink?.href && (
            <Stack.Item>
              <a href={androidLink.href} target={androidLink.target} rel="noreferrer">
                <VisuallyHidden>{androidLink.text}</VisuallyHidden>
                <Image
                  alt={androidLink.text || 'ONTDEK HET OP Google Play'}
                  height={imageHeight}
                  width={imageWidth}
                  src={`${getStaticPrefix()}/images/eneco/google-play-badge.png`}
                />
              </a>
            </Stack.Item>
          )}
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

export default StoreButtons;
